import { usePageLoading } from '@/hooks/usePageLoading';
import { stanNotify } from '@/lib';
import { SentryLogOptions } from '@/types';

import { QueryCache, QueryClient, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useAuthStore } from '@/stores';

export const queryClient = new QueryClient({
    queryCache: new QueryCache({
        onError: (error, query) => {
            const meta = query.meta as StanQueryMeta;
            if (isAxiosError(error) && error.response?.status === 401) {
                return;
            }
            if (meta?.error) {
                const customError = meta.error;
                const options: SentryLogOptions = {
                    context: {
                        error: { message: error.message },
                    },
                };
                const stateError = query.state.error;
                if (isAxiosError(stateError)) {
                    options.context = {
                        ...options.context,
                        request: {
                            url: stateError.config?.url,
                            method: stateError.config?.method,
                            data: stateError.config?.data,
                            headers: JSON.stringify(stateError.config?.headers.toJSON()),
                            params: JSON.stringify(stateError.config?.params),
                            response: JSON.stringify(stateError.response?.data),
                            status: stateError.response?.status,
                        },
                    };
                }
                if (customError.feature) {
                    options.tags = { feature: customError.feature };
                }
                stanNotify({
                    type: 'error',
                    title: customError.title || 'Oops!',
                    text: customError.message || 'Something went wrong. Please try again later.',
                    duration: 5000,
                    error: {
                        message: customError.sentryMessage,
                        options,
                    },
                });
            }
        },
    }),
});

// Base interface for Stan query options
interface StanQueryMeta extends Record<string, unknown> {
    error: {
        sentryMessage: string;
        message?: string;
        title?: string;
        feature?: string;
    };
}

// Interface for forced parameters that can't be overridden
interface StanForcedOptions {
    staleTime?: number;
    gcTime?: number;
    retry?: UseQueryOptions['retry'];
    enabled?: boolean;
}

// Main StanQueryOptions interface
export interface StanQueryOptions<TData = unknown, TError = Error> extends UseQueryOptions<TData, TError> {
    meta: StanQueryMeta;
}

// Default forced options that will be applied to all Stan queries
const DEFAULT_OPTIONS: StanForcedOptions = {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
};

export const useStanQuery = <TData = unknown, TError = Error>(
    query: StanQueryOptions<TData, TError>,
    skipPageLoading = false
) => {
    // Subscribe reactively to auth state changes
    const isAuthenticated = useAuthStore(state => state.isAuthenticated);
    const storeInitialized = useAuthStore(state => state.storeInitialized);
    const token = useAuthStore(state => state.token);

    // Extract meta from the query options
    const { meta, ...queryOptions } = query;
    const defaultRetry = (failureCount: number, error: TError) => {
        if (isAxiosError(error) && error.response?.status === 401) {
            return false;
        }
        return failureCount < 2;
    };

    // Build the final query options by merging user options with forced options
    const finalQueryOptions: UseQueryOptions<TData, TError> = {
        ...DEFAULT_OPTIONS,
        ...queryOptions,
        meta, // Include the meta for error handling
        retry: queryOptions.retry ?? defaultRetry,
        // Check if user is authenticated AND respect any enabled flag from queryOptions
        enabled: storeInitialized && isAuthenticated && !!token && (queryOptions.enabled ?? true),
    };

    const res = useQuery<TData, TError>(finalQueryOptions);

    // Only show page loading on initial load
    const shouldShowPageLoading = res.isLoading && !skipPageLoading;
    usePageLoading(shouldShowPageLoading);
    return res;
};

// Export query hooks
export * from './useStoreData';
export * from './useUser';
export * from './useIncome';
export * from './useCurrencies';
export * from './useLoginSessions';
export * from './useLoginSessionMutations';
export * from './useAccountDeletion';
export * from './useUserPreferencesMutation';
export * from './usePageMutations';
export * from './useChallenge';
